import { format, isValid } from 'date-fns';

/**
 * Converts Excel serial number (as string) to JavaScript Date
 * Excel dates are number of days since January 1, 1900
 */
export function excelSerialToDate(serialString: string): Date {
  const serial = Number.parseFloat(serialString);
  if (Number.isNaN(serial)) {
    throw new Error(`Invalid Excel serial number: ${serialString}`);
  }

  // Excel's epoch is January 1, 1900 (serial 1)
  // JavaScript's epoch is January 1, 1970
  const excelEpoch = new Date(1900, 0, 1);
  const daysSinceEpoch = serial - 1; // Subtract 1 because Excel counts from 1

  // Add the days to the epoch
  const result = new Date(excelEpoch);
  result.setDate(result.getDate() + daysSinceEpoch);

  return result;
}

/**
 * Safely converts Excel serial number to formatted date string
 */
export function formatExcelDate(serialString: string, formatString = 'M/d/yyyy'): string {
  try {
    const date = excelSerialToDate(serialString);
    if (isValid(date)) {
      return format(date, formatString);
    }
  } catch (error) {
    console.warn(`Failed to convert Excel serial ${serialString} to date:`, error);
  }
  return 'Invalid Date';
}

/**
 * Safely parses amount string to number
 */
export function parseAmount(amountString: string): number {
  if (!amountString || typeof amountString !== 'string') {
    return 0;
  }

  // Remove any non-numeric characters except decimal point
  const cleaned = amountString.replace(/[^\d.-]/g, '');
  const parsed = Number.parseFloat(cleaned);

  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Validates if coordinates are valid (not placeholder values)
 */
export function isValidCoordinates(lat: string, lng: string): boolean {
  if (!lat || !lng) return false;

  const latNum = Number.parseFloat(lat);
  const lngNum = Number.parseFloat(lng);

  // Check if coordinates are valid numbers and not placeholder values
  // Placeholder values like "98.6" and "0.67" should be filtered out
  return (
    !Number.isNaN(latNum) &&
    !Number.isNaN(lngNum) &&
    latNum >= -90 &&
    latNum <= 90 &&
    lngNum >= -180 &&
    lngNum <= 180 &&
    // Filter out obvious placeholder values
    !(latNum === 98.6 && lngNum === 0.67) &&
    !(latNum === 0 && lngNum === 0)
  );
}

/**
 * Safely converts coordinate string to number
 */
export function parseCoordinate(coord: string): number | null {
  if (!coord) return null;

  const parsed = Number.parseFloat(coord);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Formats district number consistently
 */
export function formatDistrict(district: string | undefined): string {
  if (!district) {
    return 'Unknown';
  }

  const num = Number.parseFloat(district);
  return Number.isNaN(num) ? 'Unknown' : Math.floor(num).toString();
}
